import React, {useState, useEffect, useRef, useMemo} from 'react';
import {Collapse, Card, CardHeader, CardTitle, CardBody, Table, Input, Button} from 'reactstrap';
import StudentRow from './StudentRow';
import ExtraTimeEditor from './ExtraTimeEditor';
var $ = require('jquery');
import 'datatables.net';
// import tableSort from "table-sort-js/table-sort.js";

const CardExamStudents = ({exam_name, students}) => {
    const [show, setShow] = useState(true);
    const [showExtraTime, setShowExtraTime] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const tableRef = useRef(null);
    const dataTableRef = useRef(null);

    // Filter and paginate students
    const filteredStudents = useMemo(() => {
        if (!students) return [];
        if (!searchTerm) return students;
        const term = searchTerm.toLowerCase();
        return students.filter(student => 
            student.name?.toLowerCase().includes(term) ||
            student.login_code?.toLowerCase().includes(term) ||
            student.student_id?.toString().includes(term)
        );
    }, [students, searchTerm]);

    const paginatedStudents = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        const end = start + itemsPerPage;
        return filteredStudents.slice(start, end);
    }, [filteredStudents, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);

    // Initialize DataTable only once when table is shown and data is ready
    // Don't reinitialize on page changes - let React handle DOM updates
    useEffect(() => {
        if (!show || !tableRef.current || paginatedStudents.length === 0) {
            // Clean up DataTable if hidden or empty
            if (dataTableRef.current && tableRef.current) {
                try {
                    if ($.fn.DataTable.isDataTable(tableRef.current)) {
                        $(tableRef.current).DataTable().destroy();
                    }
                } catch (e) {
                    // Ignore errors during cleanup
                }
                dataTableRef.current = null;
            }
            return;
        }
        
        // Only initialize if DataTable doesn't exist yet
        if (dataTableRef.current) {
            return; // Already initialized, don't recreate
        }
        
        // Use setTimeout to ensure React has finished DOM updates
        const timer = setTimeout(() => {
            if (!tableRef.current) return;
            
            // Check if DataTable already exists
            if ($.fn.DataTable.isDataTable(tableRef.current)) {
                dataTableRef.current = $(tableRef.current).DataTable();
                return;
            }

            // Initialize DataTable only once
            try {
                dataTableRef.current = $(tableRef.current).DataTable({
                    paging: false, // We're handling pagination ourselves
                    searching: false, // We're handling search ourselves
                    info: false,
                    order: [[0, 'asc']], // Sort by Student (serial) column, least to greatest
                });
            } catch (e) {
                console.warn('Error initializing DataTable:', e);
                dataTableRef.current = null;
            }
        }, 0);

        return () => {
            clearTimeout(timer);
            if (dataTableRef.current && tableRef.current) {
                try {
                    if ($.fn.DataTable.isDataTable(tableRef.current)) {
                        $(tableRef.current).DataTable().destroy();
                    }
                } catch (e) {
                    // Ignore errors during cleanup
                }
                dataTableRef.current = null;
            }
        };
    }, [show, currentPage, paginatedStudents.length > 0]);

    // Reset to page 1 when search or itemsPerPage changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, itemsPerPage]);

    const destroyDataTable = () => {
        if (dataTableRef.current && tableRef.current) {
            try {
                if ($.fn.DataTable.isDataTable(tableRef.current)) {
                    $(tableRef.current).DataTable().destroy();
                }
            } catch (e) {
                // Ignore errors during cleanup
            }
            dataTableRef.current = null;
        }
    };

    const handlePrevious = () => {
        destroyDataTable();
        setCurrentPage(prev => Math.max(1, prev - 1));
    };

    const handleNext = () => {
        destroyDataTable();
        setCurrentPage(prev => Math.min(totalPages, prev + 1));
    };

    const handlePageSelect = (page) => {
        destroyDataTable();
        setCurrentPage(page);
    };

    return (
        <Card>
            <CardHeader
                onClick={() => {
                    setShow(!show);
                }}
                className='pointer'
            >
                <CardTitle className='text-white'>
                    {exam_name} ({filteredStudents.length} {filteredStudents.length === 1 ? 'student' : 'students'})
                </CardTitle>
            </CardHeader>
            <Collapse isOpen={show}>
                <CardBody>
                    {(students && students.length > 0) && (
                        <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap">
                            <div className="mb-2" style={{flex: '1 1 300px', minWidth: '250px'}}>
                                <Input
                                    type="text"
                                    placeholder="Search students by name, login code, or ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="mb-2 d-flex align-items-center">
                                <label htmlFor="itemsPerPage" className="me-2 mb-0">
                                    Show:
                                </label>
                                <Input
                                    type="select"
                                    id="itemsPerPage"
                                    value={itemsPerPage}
                                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                    style={{width: 'auto', display: 'inline-block'}}
                                >
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={200}>200</option>
                                    <option value={filteredStudents.length}>All</option>
                                </Input>
                            </div>
                        </div>
                    )}
                    {students && students.length > 0 && (
                        <div className='mb-3'>
                            <Button
                                color='primary'
                                outline
                                size='sm'
                                onClick={() => setShowExtraTime(v => !v)}
                            >
                                {showExtraTime
                                    ? 'Hide extended time'
                                    : 'Set extended time (accommodations)'}
                            </Button>
                        </div>
                    )}
                    {showExtraTime && (
                        <ExtraTimeEditor
                            key={`${searchTerm}:${filteredStudents.length}`}
                            students={filteredStudents}
                        />
                    )}
                    <Table hover responsive className='studentsTable' innerRef={tableRef}>
                        <thead>
                            <tr>
                                <th>Student</th>
                                <th>Login Code</th>
                                <th>Name</th>
                                <th>MC</th>
                                <th>FR</th>
                                <th>Scored</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedStudents.length > 0 ? (
                                paginatedStudents.map((student, i) => (
                                    <StudentRow key={student.student_id || i} student={student} />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center">
                                        {searchTerm ? 'No students found matching your search.' : 'No students found.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                    {filteredStudents.length > itemsPerPage && (
                        <div className="mt-4 pt-3 border-top">
                            <div className="d-flex justify-content-between align-items-center flex-wrap">
                                <div className="text-muted small mb-2 mb-md-0">
                                    Showing <strong>{(currentPage - 1) * itemsPerPage + 1}</strong> to <strong>{Math.min(currentPage * itemsPerPage, filteredStudents.length)}</strong> of <strong>{filteredStudents.length}</strong> students
                                </div>
                                <nav aria-label="Student pagination">
                                    <div className="d-flex align-items-center">
                                        <button 
                                            className="btn btn-sm btn-outline-secondary" 
                                            onClick={handlePrevious}
                                            disabled={currentPage === 1}
                                            style={{ minWidth: '75px' }}
                                        >
                                            Previous
                                        </button>
                                        {totalPages > 1 && (
                                            <div className="d-flex align-items-center mx-2">
                                                {(() => {
                                                    const getPageNumbers = () => {
                                                        const maxButtons = 5;
                                                        if (totalPages <= maxButtons) {
                                                            return Array.from({ length: totalPages }, (_, i) => i + 1);
                                                        }
                                                        
                                                        const pages = [];
                                                        let startPage = Math.max(1, currentPage - 2);
                                                        let endPage = Math.min(totalPages, startPage + maxButtons - 1);
                                                        
                                                        if (endPage - startPage < maxButtons - 1) {
                                                            startPage = Math.max(1, endPage - maxButtons + 1);
                                                        }
                                                        
                                                        if (startPage > 1) {
                                                            pages.push(1);
                                                            if (startPage > 2) pages.push('...');
                                                        }
                                                        
                                                        for (let i = startPage; i <= endPage; i++) {
                                                            pages.push(i);
                                                        }
                                                        
                                                        if (endPage < totalPages) {
                                                            if (endPage < totalPages - 1) pages.push('...');
                                                            pages.push(totalPages);
                                                        }
                                                        
                                                        return pages;
                                                    };
                                                    
                                                    const pageNumbers = getPageNumbers();
                                                    
                                                    return pageNumbers.map((page, idx) => {
                                                        if (page === '...') {
                                                            return (
                                                                <span key={`ellipsis-${idx}`} className="px-2 text-muted">
                                                                    ...
                                                                </span>
                                                            );
                                                        }
                                                        return (
                                                            <button
                                                                key={page}
                                                                className={`btn btn-sm ${currentPage === page ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                                onClick={() => handlePageSelect(page)}
                                                                style={{ minWidth: '36px', marginLeft: '2px', marginRight: '2px' }}
                                                            >
                                                                {page}
                                                            </button>
                                                        );
                                                    });
                                                })()}
                                            </div>
                                        )}
                                        <span className="text-muted small mx-2 d-none d-md-inline">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button 
                                            className="btn btn-sm btn-outline-secondary" 
                                            onClick={handleNext}
                                            disabled={currentPage === totalPages}
                                            style={{ minWidth: '75px' }}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </nav>
                            </div>
                        </div>
                    )}
                </CardBody>
            </Collapse>
        </Card>
    );
};

export default CardExamStudents;
